import type { CreditEntry, Membership } from "./types";

/** UTC `YYYY-MM-DD`, matching the server's `Date.utc_today()`. */
export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Whether a membership period covers `onDate` (default: today, UTC). */
export function isMembershipActive(
  membership: Membership,
  onDate: string = todayIso(),
): boolean {
  if (membership.cancelled_at) return false;
  return membership.starts_on <= onDate && membership.ends_on >= onDate;
}

/** The active period from a list (which the API returns newest first). */
export function activeMembership(
  memberships: Membership[],
  onDate?: string,
): Membership | null {
  return memberships.find((m) => isMembershipActive(m, onDate)) ?? null;
}

/** "+3" / "−1" — a signed credit amount for the ledger. */
export function formatCreditAmount(amount: number): string {
  return amount > 0 ? `+${amount}` : String(amount);
}

/** Add `days` to an ISO `YYYY-MM-DD` date, returning the same format. */
export function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * The student's latest open (non-cancelled) membership period, or `null`.
 * This is the period a renewal grows in place.
 */
export function latestMembership(memberships: Membership[]): Membership | null {
  const open = memberships.filter(
    (m) =>
      !m.cancelled_at && typeof m.ends_on === "string" && m.ends_on.length === 10,
  );

  if (open.length === 0) return null;
  return open.reduce((latest, m) => (m.ends_on > latest.ends_on ? m : latest));
}

/** Ledger kind → i18n key for its human label. */
export function creditKindLabelKey(kind: CreditEntry["kind"]): string {
  return `teacher.ledgerKind.${kind}`;
}

/**
 * Funding kinds a teacher may reverse (issue #311). `:spend` is never
 * reversible, and a `:reversal` is not reversible either — corrections
 * reach depth one, so an over-recharge cannot be reversed twice.
 */
const REVERSIBLE_KINDS: ReadonlySet<CreditEntry["kind"]> = new Set([
  "recharge",
  "refund",
  "comp",
]);

export function isReversible(entry: CreditEntry): boolean {
  return REVERSIBLE_KINDS.has(entry.kind);
}

/**
 * How much of `entry` is still reversible.
 *
 * For an expiring grant (issue #327) the server's `remaining` is the
 * authoritative cap — it already nets out spends and prior reversals. For
 * legacy entries with no allocation, fall back to `|amount|` minus what has
 * already been reversed (the teacher ledger lists reversals alongside their
 * target, so `entries` is the full ledger).
 */
export function remainingReversible(
  entry: CreditEntry,
  entries: CreditEntry[],
): number {
  if (entry.remaining != null) return Math.max(entry.remaining, 0);

  const reversed = entries
    .filter((e) => e.kind === "reversal" && e.reverses_id === entry.id)
    .reduce((sum, e) => sum + Math.abs(e.amount), 0);

  return Math.max(Math.abs(entry.amount) - reversed, 0);
}
